// src/pages/QRDesignPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { CardService } from '../services/cardService';
import { QRService, QRDesignData } from '../services/qrService';
import QRCode from '../components/QRCode';
import ColorPicker from '../components/ColorPicker';
import { downloadQRPng, downloadQRSvg } from '../utils/qrDownload';

// 모바일 앱과 동일한 색상 옵션
const PREDEFINED_COLORS = [
  { name: 'pureBlack', value: '#000000' },
  { name: 'coolMint', value: '#00C2A8' },
  { name: 'deepBlue', value: '#1E3A8A' },
  { name: 'sunsetOrange', value: '#FF6B57' },
  { name: 'mutedViolet', value: '#7D6AFF' },
  { name: 'warmSand', value: '#F4D58D' },
  { name: 'modernBlue', value: '#3B82F6' },
  { name: 'vibrantGreen', value: '#10B981' },
  { name: 'coralRed', value: '#EF4444' },
  { name: 'royalPurple', value: '#8B5CF6' },
];

const BACKGROUND_COLORS = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F2F2F7' },
  { name: 'Light Blue', value: '#E6F3FF' },
  { name: 'Light Green', value: '#E6FFED' },
  { name: 'Light Pink', value: '#FFE6F0' },
  { name: 'Light Yellow', value: '#FFFACD' },
];

const QRDesignPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { cardId } = useParams<{ cardId: string }>();

  const [qrColor, setQrColor] = useState('#000000');
  const [qrBackgroundColor, setQrBackgroundColor] = useState('#FFFFFF');
  const [useProfileImageAsLogo, setUseProfileImageAsLogo] = useState(false);
  // 미리보기용 URL (저장된 https URL 또는 새로 고른 파일의 로컬 미리보기)
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(null);
  // 아직 업로드되지 않은, 새로 선택한 로고 파일. 저장 시 Storage에 업로드한다.
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDownloading, setIsDownloading] = useState<'png' | 'svg' | null>(null);
  const [cardData, setCardData] = useState<any>(null);
  const [qrValue, setQrValue] = useState('');
  const [profileUrl, setProfileUrl] = useState('');

  const fileInputRef = useRef<HTMLInputElement>(null);

  // QR 디자인은 모든 플랜(만료 포함)에서 사용 가능한 무료 기능 — 구독 제한 없음

  // 안전한 번역 함수
  const safeT = (key: string, fallback: string) => {
    try {
      const result = t(key);
      return result === key ? fallback : result;
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    const loadCardData = async () => {
      if (!user?.uid || !cardId) return;

      try {
        const cards = await CardService.getUserCards(user.uid);
        const card = cards.find(c => c.id === cardId);
        
        if (!card) {
          console.error('Card not found:', cardId);
          return;
        }
        
        setCardData(card);

        // 프로필 주소와 동일한 로직으로 QR 기본 주소 계산 (대시보드 표시 주소와 일치)
        const anyCard = card as any;
        let profile = '';
        if (anyCard.customPath) {
          profile = `https://scanvcf.com/${anyCard.customPath}`;
        } else if (anyCard.customDomain) {
          profile = `https://${anyCard.customDomain}`;
        } else if (anyCard.shareUrl) {
          profile = anyCard.shareUrl;
        } else {
          profile = `https://scanvcf.com/qr/${card.id}`;
        }
        setProfileUrl(profile);

        // 기존 QR 디자인 데이터 마이그레이션
        await QRService.migrateQRDesignData(user.uid, cardId);

        const qrDesign = await QRService.getQRDesign(user.uid, cardId);
        if (qrDesign) {
          setQrColor(qrDesign.qrColor);
          setQrBackgroundColor(qrDesign.qrBackgroundColor);
          setCustomLogoUrl(qrDesign.qrLogoUrl || null);
        }

        // 저장된 주소가 사용자가 직접 수정한 값이면 유지, 과거 자동 생성 주소면 프로필 주소로 교체
        const legacyAutoUrls = [
          `https://scanvcf.com/qr/${card.id}`,
          anyCard.customUrl ? `https://scanvcf.com/u/${anyCard.customUrl}` : null,
          `https://scanvcf.com/card/${user.uid}/${card.id}`
        ].filter(Boolean);
        const savedValue = qrDesign?.qrValue;
        setQrValue(savedValue && !legacyAutoUrls.includes(savedValue) ? savedValue : profile);
      } catch (error) {
        console.error('Failed to load card data:', error);
      }
    };

    loadCardData();
  }, [user?.uid, cardId]);

  const handleLogoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert(safeT('qrDesign.logoInvalidType', 'Please select an image file.'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(safeT('qrDesign.logoTooLarge', 'The logo image must be 5MB or smaller.'));
      return;
    }

    // 저장 시 Storage로 업로드할 원본 파일 보관
    setPendingLogoFile(file);

    // 화면 미리보기용 (Firestore에는 저장하지 않는다)
    const reader = new FileReader();
    reader.onload = (e) => {
      setCustomLogoUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);

    // 같은 파일을 다시 선택해도 onChange가 발생하도록 초기화
    event.target.value = '';
  };

  const handleReset = () => {
    setQrColor('#000000');
    setQrBackgroundColor('#FFFFFF');
    setCustomLogoUrl(null);
    setPendingLogoFile(null);
    setUseProfileImageAsLogo(false);
    if (profileUrl) {
      setQrValue(profileUrl);
    }
  };

  const handleDownload = async (format: 'png' | 'svg') => {
    if (!qrValue || isDownloading) return;

    setIsDownloading(format);
    try {
      const logoUrl = customLogoUrl || (useProfileImageAsLogo && cardData?.photoUrl ? cardData.photoUrl : undefined);
      const options = {
        value: qrValue,
        color: qrColor,
        backgroundColor: qrBackgroundColor,
        logoUrl,
        filename: `${cardData?.name || 'scanvcf'}-qr`
      };

      const result = format === 'png' ? await downloadQRPng(options) : await downloadQRSvg(options);

      if (logoUrl && !result.logoIncluded) {
        alert(safeT('qrDesign.downloadLogoFailed', 'The logo could not be included. The QR code was downloaded without the logo.'));
      }
    } catch (error) {
      console.error('Failed to download QR code:', error);
      alert(safeT('qrDesign.downloadError', 'An error occurred while downloading the QR code.'));
    } finally {
      setIsDownloading(null);
    }
  };

  const handleSave = async () => {
    if (!user?.uid || !cardId) return;

    setIsSaving(true);
    try {
      let logoUrl: string | null = null;

      if (pendingLogoFile) {
        // 새로 고른 로고는 Storage에 업로드하고 URL만 Firestore에 저장한다
        logoUrl = await QRService.uploadQRLogo(user.uid, cardId, pendingLogoFile);
        setPendingLogoFile(null);
        setCustomLogoUrl(logoUrl);
      } else if (customLogoUrl && !customLogoUrl.startsWith('data:')) {
        // 이미 업로드되어 있는 로고 URL은 그대로 유지
        logoUrl = customLogoUrl;
      } else if (useProfileImageAsLogo && cardData?.photoUrl) {
        logoUrl = cardData.photoUrl;
      }

      const qrDesignData: QRDesignData = {
        qrColor,
        qrBackgroundColor,
        qrLogoUrl: logoUrl || undefined,
        qrValue,
        createdAt: new Date()
      };

      await QRService.updateQRDesign(user.uid, cardId, qrDesignData);

      alert(safeT('qrDesign.saveComplete', 'QR design has been saved.'));
      navigate('/dashboard');
    } catch (error: any) {
      console.error('Failed to save QR design:', error);
      // 원인 파악이 가능하도록 실제 오류 메시지를 함께 보여준다
      const detail = error?.message ? `\n\n${error.message}` : '';
      alert(safeT('qrDesign.saveError', 'An error occurred while saving.') + detail);
    } finally {
      setIsSaving(false);
    }
  };

  if (!cardData) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{safeT('common.loading', 'Loading card information...')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {safeT('qrDesign.title', 'QR Code Design')}
          </h1>
          <p className="text-gray-600">
            {safeT('qrDesign.subtitle', 'Customize the color and logo of your QR code')}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* QR 코드 미리보기 */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('profilePreview.title', 'Preview')}
            </h2>
            <div 
              className="flex justify-center p-6 rounded-lg"
              style={{ backgroundColor: qrBackgroundColor }}
            >
              <QRCode
                value={qrValue}
                size={256}
                color={qrColor}
                backgroundColor={qrBackgroundColor}
                logoUrl={customLogoUrl || (useProfileImageAsLogo && cardData?.photoUrl ? cardData.photoUrl : undefined)}
                logoSize={80}
                logoBackgroundColor="white"
                logoMargin={8}
                logoBorderRadius={8}
                ecl="H"
              />
            </div>

            {/* 인쇄용 고해상도 다운로드 */}
            <div className="mt-4">
              <div className="flex space-x-3">
                <button
                  onClick={() => handleDownload('png')}
                  disabled={isDownloading !== null}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isDownloading === 'png'
                    ? safeT('qrDesign.downloading', 'Downloading...')
                    : safeT('qrDesign.downloadPng', 'PNG (High-Res)')}
                </button>
                <button
                  onClick={() => handleDownload('svg')}
                  disabled={isDownloading !== null}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isDownloading === 'svg'
                    ? safeT('qrDesign.downloading', 'Downloading...')
                    : safeT('qrDesign.downloadSvg', 'SVG (Vector)')}
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 text-center">
                {safeT('qrDesign.downloadHint', 'For business cards and print, use the high-resolution PNG or vector SVG.')}
              </p>
            </div>
          </div>

          {/* 디자인 옵션 */}
          <div className="bg-white rounded-lg shadow-md p-6">
            {/* QR 링크 주소 */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-700 mb-2">
                {safeT('qrDesign.linkAddress', 'QR Link Address')}
              </h3>
              <input
                type="url"
                value={qrValue}
                onChange={(e) => setQrValue(e.target.value)}
                placeholder="https://"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-2 flex items-center justify-between gap-2">
                <p className="text-xs text-gray-500">
                  {safeT('qrDesign.linkAddressHint', 'The address people open when they scan this QR code.')}
                </p>
                {profileUrl && qrValue !== profileUrl && (
                  <button
                    onClick={() => setQrValue(profileUrl)}
                    className="flex-shrink-0 text-xs text-blue-600 hover:text-blue-800 underline"
                  >
                    {safeT('qrDesign.useProfileAddress', 'Use profile address')}
                  </button>
                )}
              </div>
            </div>

            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {safeT('qrDesign.colorSelectionTitle', 'Color Selection')}
            </h2>

            {/* QR 코드 색상 */}
            <div className="mb-6">
              <ColorPicker
                color={qrColor}
                onColorChange={setQrColor}
                showSwatches={true}
                swatches={PREDEFINED_COLORS}
                label={safeT('qrDesign.qrCodeColor', 'QR Code Color')}
              />
            </div>

            {/* 배경 색상 */}
            <div className="mb-6">
              <ColorPicker
                color={qrBackgroundColor}
                onColorChange={setQrBackgroundColor}
                showSwatches={true}
                swatches={BACKGROUND_COLORS}
                label={safeT('qrDesign.backgroundColor', 'Background Color')}
              />
            </div>

            {/* 로고 옵션 */}
            <div className="mb-6">
              <h3 className="text-md font-medium text-gray-700 mb-3">
                {safeT('qrDesign.logoSectionTitle', 'Logo Settings')}
              </h3>
              
              {/* 프로필 이미지 사용 */}
              {cardData?.photoUrl && (
                <div className="mb-3">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={useProfileImageAsLogo}
                      onChange={(e) => setUseProfileImageAsLogo(e.target.checked)}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700">
                      {safeT('qrDesign.useProfileAsLogo', 'Use profile image as logo')}
                    </span>
                  </label>
                </div>
              )}

              {/* 커스텀 로고 업로드 */}
              <div className="mb-3">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  {safeT('qrDesign.customLogoButton', 'Upload Custom Logo')}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoUpload}
                  className="hidden"
                />
              </div>

              {/* 선택된 로고 미리보기 */}
              {customLogoUrl && (
                <div className="flex items-center p-3 bg-gray-50 rounded-md">
                  <span className="text-sm text-gray-600 mr-3">
                    {safeT('qrDesign.selectedLogoLabel', 'Selected Logo:')}
                  </span>
                  <img
                    src={customLogoUrl}
                    alt="Selected logo"
                    className="w-10 h-10 rounded object-cover"
                  />
                  <button
                    onClick={() => {
                      setCustomLogoUrl(null);
                      setPendingLogoFile(null);
                    }}
                    className="ml-auto text-sm text-red-600 hover:text-red-800 underline"
                  >
                    {safeT('qrDesign.removeLogo', 'Remove')}
                  </button>
                </div>
              )}
            </div>

            {/* 액션 버튼들 */}
            <div className="flex space-x-3">
              <button
                onClick={handleReset}
                className="flex-1 px-4 py-2 border border-red-300 text-red-600 rounded-md hover:bg-red-50"
              >
                {safeT('qrDesign.resetAllButton', 'Reset')}
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="lux-btn-gold flex-1 px-4 py-2.5 rounded-full font-semibold disabled:opacity-50"
              >
                {isSaving ? safeT('qrDesign.saving', 'Saving...') : safeT('common.save', 'Save')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRDesignPage;